import type { Simplify } from 'type-fest';

import type { Monitorable } from './monitorable.js';

import { createFlatStore } from '@preact-signals/utils';
import { useMemo } from 'react';

export type StateStore<T extends Record<string, any>> = Simplify<
  T & { update(updates: Partial<T>): void } & Monitorable
>;

/**
 * Creates a Signal-based state object with a partial `update` method.
 *
 * @param initialState - The initial state object.
 * @returns A partial state object with an `update` method, triggering reactivity only on the updated properties and their dependencies.
 */
export function createState<T extends Record<string, any>>(initialState: T): StateStore<T> {
  const [state, update] = createFlatStore(initialState);
  Object.assign(initialState, { update });
  return state as unknown as StateStore<T>;
}

/**
 * Creates a state store that is reactive to the initial state and the updates.
 * @param initialState - The initial state object, it is only used once, when the component is mounted.
 * @returns A state store object with an `update` method, triggering reactivity only on the updated properties and their dependencies.
 */
export function useStateStore<T extends Record<string, any>>(initialState: T): StateStore<T> {
  const state = useMemo(() => createState(initialState), []);
  return state;
}
